import { useEffect, useState } from 'react';
import { ApiError, type components } from '@awj/api-client';
import { Button, useToast } from '@awj/ui';

import { FieldHead } from '~/components/InfoHint.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useSaveSettings } from '~/lib/settings-queries.ts';

type Group = components['schemas']['SettingGroupResponse'];

/** What the server sends back in place of a secret it is not going to reveal. */
const SECRET_MASK = '••••••••';

/**
 * One group of settings, saved as a unit.
 *
 * The control follows the kind the server declares, so a boolean is a checkbox and a fee is a
 * number field — the panel never has to keep its own copy of what each setting is.
 *
 * A secret arrives masked and is sent back untouched unless it is retyped. That is the whole
 * contract: the value never leaves the server, and leaving the box alone means "keep what is
 * there" rather than "set it to eight bullets".
 */
export function SettingsGroup({ group }: { group: Group }) {
  const { t } = useI18n();
  const toast = useToast();
  const save = useSaveSettings();

  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the server's copy changes, so a save elsewhere is reflected here.
  useEffect(() => {
    setValues(Object.fromEntries(group.values.map((setting) => [setting.key, setting.value ?? ''])));
    setError(null);
  }, [group]);

  const dirty = group.values.some((setting) => (values[setting.key] ?? '') !== (setting.value ?? ''));

  const fields = group.values.filter((setting) => setting.kind !== 'Boolean');
  const toggles = group.values.filter((setting) => setting.kind === 'Boolean');

  // The settings the API refused, by key. Marked on the field itself, in the panel's language.
  const [invalid, setInvalid] = useState<string[]>([]);

  function set(key: string, value: string) {
    setValues((current) => ({ ...current, [key]: value }));
    setError(null);
    setInvalid((current) => current.filter((other) => other !== key));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    try {
      await save.mutateAsync({
        group: group.group,
        values: Object.fromEntries(
          group.values.map((setting) => [
            setting.key,
            // An untouched secret goes back as the mask, which the API reads as "unchanged"; a box
            // emptied on purpose goes back as null, which clears it.
            values[setting.key] === '' ? null : (values[setting.key] ?? null),
          ]),
        ),
      });

      toast.show({ title: t('settings.saved'), tone: 'success' });
    } catch (caught) {
      const refused = caught instanceof ApiError ? Object.keys(caught.fieldErrors) : [];

      setInvalid(refused);
      setError(
        refused.length > 0
          ? t('settings.checkMarked' as never)
          : caught instanceof ApiError
            ? (caught.problem.detail ?? t('settings.couldNotSave'))
            : t('common.somethingWentWrong'),
      );
    }
  }

  return (
    <form className="card settings-group" onSubmit={submit}>
      <div className="card__head">
        <h2 className="section-label">{t(`settingGroup.${group.group}` as 'settingGroup.Store')}</h2>
        <Button type="submit" size="sm" loading={save.isPending} disabled={!dirty}>
          {dirty ? t('common.save') : t('settings.noChanges')}
        </Button>
      </div>

      {/*
        Switches below the fields, not among them.
        
        Everything shared one grid, and a row of it could hold a two-line textarea beside a lone
        checkbox — which left the checkbox floating in the middle of a tall cell with nothing to
        line up against. They are also two different kinds of decision: a field is something to
        fill in, a switch is something to turn on, and a group reads faster when the switches are
        a short list of their own.
      */}
      {fields.length ? (
        <div className="settings-grid">
          {fields.map((setting) => {
            const id = `${group.group}-${setting.key}`;
            const value = values[setting.key] ?? '';
            const multiline = setting.kind === 'MultilineText';
            // What kind of value goes here is a note, behind the "i". Whether a secret is stored
            // is the field's state, so it stays on the page — see below.
            const note = setting.isSecret ? '' : hint(setting.kind, false, setting.hasValue, t).trim();
            const refused = invalid.includes(setting.key);

            return (
              <div
                className={`field settings-field ${wide(setting.kind) ? 'settings-field--wide' : ''}`}
                key={setting.key}
              >
                <FieldHead
                  htmlFor={id}
                  label={label(group.group, setting.key, t)}
                  hint={note || undefined}
                  hintId={`${id}-hint`}
                />

                {setting.kind === 'Locale' ? (
                  // Two possible answers: a list, so there is nothing to mistype.
                  <Select
                    id={id}
                    value={value}
                    onChange={(next) => set(setting.key, next)}
                    options={[
                      { value: 'ar', label: 'العربية' },
                      { value: 'en', label: 'English' },
                    ]}
                  />
                ) : multiline ? (
                  <textarea
                    id={id}
                    className="awj-textarea"
                    /* Two lines, not three: these take the whole row now, and an address that
                       fits on one line should not sit in a box built for a paragraph. */
                    rows={2}
                    value={value}
                    aria-describedby={note ? `${id}-hint` : undefined}
                    onChange={(event) => set(setting.key, event.target.value)}
                  />
                ) : (
                  <input
                    id={id}
                    className={`awj-input ${numeric(setting.kind) ? 'numeric' : ''}`}
                    aria-invalid={refused || undefined}
                    type={inputType(setting.kind, setting.isSecret)}
                    inputMode={numeric(setting.kind) ? 'decimal' : undefined}
                    step={setting.kind === 'Money' ? '0.01' : setting.kind === 'Percentage' ? '0.001' : undefined}
                    dir={setting.kind === 'Url' || setting.kind === 'Email' ? 'ltr' : undefined}
                    value={value}
                    aria-describedby={note ? `${id}-hint` : undefined}
                    autoComplete={setting.isSecret ? 'new-password' : 'off'}
                    onFocus={(event) => {
                      /*
                       * Select the mask, so typing replaces it rather than appending to eight
                       * bullets. It used to be *cleared* on focus — and merely tabbing through
                       * the box then sent it back empty, which the API stores, so saving any
                       * other field in the group wiped the secret.
                       */
                      if (setting.isSecret && event.target.value === SECRET_MASK) {
                        event.target.select();
                      }
                    }}
                    onChange={(event) => set(setting.key, event.target.value)}
                  />
                )}

                {setting.isSecret ? (
                  <span className="field__hint">{hint(setting.kind, true, setting.hasValue, t)}</span>
                ) : null}

                {refused ? (
                  <span className="field__error" role="alert">
                    {t('settings.invalidValue' as never, { expected: note || hint(setting.kind, false, setting.hasValue, t) })}
                  </span>
                ) : null}

                {/* Said on the page, not behind the "i": an empty list changes where mail goes. */}
                {setting.key === 'AdminRecipients' && value.trim() === '' ? (
                  <span className="field__hint">{t('settings.recipientsFallback' as never)}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {toggles.length ? (
        <div className="settings-toggles">
          {toggles.map((setting) => {
            const id = `${group.group}-${setting.key}`;

            return (
              <label className="checkbox settings-toggle" key={setting.key} htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={(values[setting.key] ?? '') === 'true'}
                  onChange={(event) => set(setting.key, event.target.checked ? 'true' : 'false')}
                />
                <span>{label(group.group, setting.key, t)}</span>
              </label>
            );
          })}
        </div>
      ) : null}

      {error ? (
        <p className="bilingual__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/**
 * What a setting is called, in the language the panel is being read in.
 *
 * The keys are C# identifiers and were being shown as such —
 * `AutoCancelUnpaidAfterHours` split on its capitals. That is legible in English if you already
 * know what it means, and is simply English in an Arabic panel. Every setting in the catalogue
 * now carries a name in both languages instead.
 *
 * Group-qualified, so two groups can hold a `MessageEn` without one naming the other's, and
 * `humanise` stays as the safety net: a setting added to the catalogue before its label reads as
 * its own name rather than as a dotted path.
 */
function label(group: string, key: string, t: (key: never) => string): string {
  const message = `settingKey.${group}.${key}`;
  const named = t(message as never);

  return named === message ? humanise(key) : named;
}

/**
 * Whether a setting takes the whole row rather than one of the two columns.
 *
 * An address, an offline notice, a comma-separated list — all of them say something that fits on
 * one line and wraps three times in half a column. The kind is enough to know it; no setting has
 * to declare its own width.
 */
function wide(kind: string): boolean {
  return kind === 'MultilineText' || kind === 'EmailList' || kind === 'EmirateList';
}

/** `FreeShippingThreshold` → `Free shipping threshold`, for a key with no label yet. */
function humanise(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (first) => first.toUpperCase());
}

function numeric(kind: string): boolean {
  return kind === 'Number' || kind === 'Money' || kind === 'Percentage';
}

function inputType(kind: string, isSecret: boolean): string {
  if (isSecret) {
    return 'password';
  }

  if (numeric(kind)) {
    return 'number';
  }

  return kind === 'Email' ? 'email' : kind === 'Url' ? 'url' : 'text';
}

function hint(
  kind: string,
  isSecret: boolean,
  hasValue: boolean,
  t: (key: never, values?: Record<string, string>) => string,
): string {
  if (isSecret) {
    return hasValue ? t('settings.secretSet' as never) : t('settings.secretUnset' as never);
  }

  return t(`settingKind.${kind}` as never);
}
